import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PatientLayout, HelpDrawer, PatientLoading, PatientError } from '@/components/PatientLayout';
import { Check, Clock, FileCheck2, ArrowRight, ScanLine, Loader2, AlertTriangle, Pencil, CheckCircle2, Building2, CalendarClock, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useLang } from '@/lib/i18n';

const REVISION_CATEGORY_VALUES = ['my_details', 'dates', 'employer', 'other'];

// Mirrors hr_cure.MISSING_CODES on the server. The CODES are the contract; the
// wording lives in lib/i18n.js because these pages are EN/ES and the server is
// not. `/employer-cure/options` returns the same list, so a code added there and
// not here simply does not render — safe, and visible the moment anyone looks.
const CURE_MISSING_CODES = ['incomplete_form', 'own_form', 'dates', 'frequency_duration',
  'job_functions', 'medical_facts', 'provider_details', 'other'];

// ADR 0006 decision 7: a photo of HR's letter rides the ordinary uploads path and
// is shown to the doctor as HR wrote it. Same cap as every other upload.
const MAX_LETTER_BYTES = 10 * 1024 * 1024;

async function attachLetter(token, file) {
  const fd = new FormData();
  fd.append('file', file);
  await api.post(`/patient/by-token/${token}/employer-cure/letter`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } });
}

// The patient's LOCAL calendar date. toISOString() is UTC, so a US evening
// defaulted the notice date to tomorrow (and `max` refused today's letter).
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * "My employer says something is missing" — the seven-day cure loop (LB-CURE).
 *
 * 29 CFR 825.305(c) gives the employee seven calendar days to supply whatever an
 * employer calls missing, and the employee is who the employer writes to. So this
 * is the patient's card, and the only thing it asks of them is the date on the
 * letter, a tap or two, and a sentence. Everything after that happens between
 * their doctor and the employer.
 *
 * Two states, never both: report one, or watch the one that is running.
 */
function EmployerCureCard({ token, canReport, cure, requests, onSubmitted }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noticeDate, setNoticeDate] = useState(today);
  const [missing, setMissing] = useState([]);
  const [message, setMessage] = useState('');
  const [letter, setLetter] = useState(null);

  const toggle = (code) => setMissing((prev) =>
    prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);

  const pickLetter = (file) => {
    if (!file) { setLetter(null); return; }
    if (file.size > MAX_LETTER_BYTES) { toast.error(t('cure.letterTooBig')); return; }
    setLetter(file);
  };

  const submit = async () => {
    if (!message.trim()) { toast.error(t('cure.needMessage')); return; }
    setBusy(true);
    try {
      await api.post(`/patient/by-token/${token}/employer-cure`, {
        notice_date: noticeDate, missing, message: message.trim(),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cure.error'));
      setBusy(false);
      return;
    }
    // The request is filed and the clock is running whatever happens to the
    // photo — so a failed upload is reported, never allowed to undo the report.
    let photoOk = true;
    if (letter) {
      try { await attachLetter(token, letter); } catch { photoOk = false; }
    }
    if (photoOk) toast.success(t('cure.sent')); else toast.warning(t('cure.letterFailed'));
    setOpen(false);
    setMissing([]);
    setMessage('');
    setLetter(null);
    setNoticeDate(today());
    setBusy(false);
    onSubmitted?.();
  };

  // Adding the photo after the fact, from the running-clock card.
  const addLetterLater = async (file) => {
    if (!file) return;
    if (file.size > MAX_LETTER_BYTES) { toast.error(t('cure.letterTooBig')); return; }
    setBusy(true);
    try {
      await attachLetter(token, file);
      toast.success(t('cure.letterSent'));
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cure.error'));
    } finally { setBusy(false); }
  };

  // The running clock. Deliberately the whole card when one is open: a patient
  // with a live deadline should not also be looking at a button to start another.
  if (cure?.open > 0) {
    const left = cure.days_left;
    const urgent = cure.urgency === 'overdue' || cure.urgency === 'today';
    const countdown = cure.urgency === 'overdue' ? t('cure.open.overdue')
      : cure.urgency === 'today' ? t('cure.open.today')
      : left === 1 ? t('cure.open.oneDay')
      : t('cure.open.days', { days: left });
    const asked = (requests || []).flatMap((r) => r?.cure?.missing || []);
    const hasLetter = (requests || []).some((r) => r?.cure?.letter);
    return (
      <Card className="rounded-2xl" data-testid="patient-cure-open-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3.5">
            <div className="h-11 w-11 rounded-xl bg-accent text-accent-foreground grid place-items-center shrink-0">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-[16px] leading-6">{t('cure.open.title')}</div>
              <Badge
                className={cn('rounded-full text-[12px] mt-1.5', urgent
                  ? 'bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)] hover:bg-[hsl(48_96%_88%)]'
                  : '')}
                variant={urgent ? undefined : 'secondary'}
                data-testid="patient-cure-countdown"
              >
                {countdown}
              </Badge>
            </div>
          </div>
          {asked.length > 0 && (
            <div className="text-sm text-muted-foreground leading-6">
              <span className="font-medium text-foreground">{t('cure.open.asked')}</span>{' '}
              {asked.map((c) => t(`cure.missing.${c}`)).join(' · ')}
            </div>
          )}
          <p className="text-sm text-muted-foreground leading-6">{t('cure.open.body')}</p>
          {hasLetter ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="patient-cure-letter-attached">
              <CheckCircle2 className="h-4 w-4 text-primary" /> {t('cure.open.letterAttached')}
            </div>
          ) : (
            <label className={cn('flex items-center justify-center gap-2 h-12 w-full rounded-xl border text-[15px] font-semibold cursor-pointer hover:bg-muted', busy && 'opacity-60 pointer-events-none')}>
              <Camera className="h-4 w-4" /> {t('cure.open.letterAdd')}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="sr-only"
                onChange={(e) => { addLetterLater(e.target.files?.[0]); e.target.value = ''; }}
                data-testid="patient-cure-letter-later"
              />
            </label>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!canReport) return null;

  return (
    <Card className="rounded-2xl" data-testid="patient-cure-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3.5">
          <div className="h-11 w-11 rounded-xl bg-accent text-accent-foreground grid place-items-center shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="text-sm text-muted-foreground leading-6">
            <span className="font-semibold text-foreground">{t('cure.title')}</span>{' '}
            {t('cure.body')}
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full h-12 rounded-xl text-[15px] font-semibold" data-testid="patient-cure-open-button">
              <Pencil className="h-4 w-4 mr-1.5" /> {t('cure.button')}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle>{t('cure.dialogTitle')}</DialogTitle>
              <DialogDescription>{t('cure.dialogSub')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium" htmlFor="cure-notice-date">{t('cure.noticeDate')}</label>
                <input
                  id="cure-notice-date"
                  type="date"
                  max={today()}
                  value={noticeDate}
                  onChange={(e) => setNoticeDate(e.target.value)}
                  className="flex h-12 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm mt-1"
                  data-testid="patient-cure-notice-date"
                />
                <p className="text-xs text-muted-foreground mt-1">{t('cure.noticeHelp')}</p>
              </div>
              <div>
                <span className="text-sm font-medium">{t('cure.what')}</span>
                <div className="grid gap-2 mt-1.5">
                  {CURE_MISSING_CODES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggle(code)}
                      aria-pressed={missing.includes(code)}
                      className={cn('text-left rounded-xl border px-3.5 py-2.5 text-sm leading-5 transition-colors',
                        missing.includes(code) ? 'border-primary bg-accent text-accent-foreground font-medium' : 'bg-card')}
                      data-testid={`patient-cure-missing-${code}`}
                    >
                      {t(`cure.missing.${code}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="cure-message">{t('cure.tellMore')}</label>
                <Textarea
                  id="cure-message"
                  rows={4}
                  value={message}
                  maxLength={1000}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('cure.placeholder')}
                  className="mt-1 rounded-xl"
                  data-testid="patient-cure-message"
                />
              </div>
              <div>
                <span className="text-sm font-medium">{t('cure.letter')}</span>
                <label className="mt-1.5 flex items-center gap-2 h-12 w-full rounded-xl border px-3.5 text-sm cursor-pointer hover:bg-muted">
                  <Camera className="h-4 w-4 shrink-0" />
                  <span className="truncate">{letter ? letter.name : t('cure.letterChoose')}</span>
                  {letter && <span className="ml-auto text-xs text-muted-foreground shrink-0">{t('cure.letterChange')}</span>}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="sr-only"
                    onChange={(e) => pickLetter(e.target.files?.[0])}
                    data-testid="patient-cure-letter"
                  />
                </label>
                <p className="text-xs text-muted-foreground mt-1">{t('cure.letterHelp')}</p>
              </div>
            </div>
            <DialogFooter>
              <Button className="w-full h-12 rounded-xl text-[15px] font-semibold" onClick={submit} disabled={busy} data-testid="patient-cure-submit">
                {busy ? t('cure.sending') : t('cure.send')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function RevisionCard({ token, revisions, draft, onSubmitted }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('dates');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  if (draft) return null;

  const submit = async () => {
    if (!message.trim()) { toast.error(t('revision.needMessage')); return; }
    setBusy(true);
    try {
      await api.post(`/patient/by-token/${token}/revision-request`, { category, message: message.trim() });
      toast.success(t('revision.sent'));
      setMessage('');
      setOpen(false);
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('revision.error'));
    } finally { setBusy(false); }
  };

  const chip = (status) => {
    if (status === 'open') return <Badge variant="secondary" className="rounded-full text-[12px] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)]">{t('revision.chip.open')}</Badge>;
    if (status === 'resolved') return <Badge className="rounded-full text-[12px]"><CheckCircle2 className="h-3 w-3 mr-1" /> {t('revision.chip.resolved')}</Badge>;
    return <Badge variant="outline" className="rounded-full text-[12px]">{t('revision.chip.dismissed')}</Badge>;
  };

  return (
    <Card className="rounded-2xl" data-testid="patient-revision-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3.5">
          <div className="h-11 w-11 rounded-xl bg-accent text-accent-foreground grid place-items-center shrink-0">
            <Pencil className="h-5 w-5" />
          </div>
          <div className="flex-1 text-sm text-muted-foreground leading-6">
            <span className="font-semibold text-foreground">{t('revision.prompt')}</span>{' '}
            {t('revision.promptBody')}
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full h-12 rounded-xl text-[15px] font-semibold" data-testid="patient-request-change-button">
              {t('revision.button')}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle>{t('revision.dialogTitle')}</DialogTitle>
              <DialogDescription>{t('revision.dialogSub')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">{t('revision.what')}</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="patient-revision-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REVISION_CATEGORY_VALUES.map((v) => <SelectItem key={v} value={v}>{t(`revision.cat.${v}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('revision.tellMore')}</label>
                <Textarea
                  rows={4}
                  className="rounded-xl mt-1 text-[15px]"
                  placeholder={t('revision.placeholder')}
                  value={message}
                  maxLength={1000}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="patient-revision-message"
                />
              </div>
            </div>
            <DialogFooter>
              <Button className="w-full h-12 rounded-xl text-[15px] font-semibold" onClick={submit} disabled={busy} data-testid="patient-revision-submit">
                {busy ? t('revision.sending') : t('revision.send')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {revisions.length > 0 && (
          <ul className="space-y-2.5" data-testid="patient-revision-list">
            {revisions.map((r) => r.category === 'doctor_question' ? (
              <DoctorQuestion key={r.id} token={token} question={r} chip={chip} onAnswered={onSubmitted} />
            ) : (
              <li key={r.id} className="rounded-xl border bg-card p-3.5" data-testid="patient-revision-item">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold">{REVISION_CATEGORY_VALUES.includes(r.category) ? t(`revision.cat.${r.category}`) : t('revision.fallbackTitle')}</span>
                  {chip(r.status)}
                </div>
                <p className="text-sm text-muted-foreground leading-6 mt-1">{r.message}</p>
                {r.status === 'resolved' && r.resolution_note && (
                  <p className="text-sm leading-6 mt-1.5 text-foreground"><span className="font-medium">{t('revision.teamReply')}</span> {r.resolution_note}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ADR 0009: in a practice with no staff step, the doctor's questions come to the
// patient on this page. The answer is stored on the question; signing closes it.
function DoctorQuestion({ token, question, chip, onAnswered }) {
  const { t } = useLang();
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const answer = async () => {
    if (!reply.trim()) { toast.error(t('question.needAnswer')); return; }
    setBusy(true);
    try {
      await api.post(`/patient/by-token/${token}/revision-request`, { reply_to: question.id, message: reply.trim() });
      toast.success(t('question.sent'));
      setReply('');
      onAnswered();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('revision.error'));
    } finally { setBusy(false); }
  };
  const open = question.status === 'open';
  return (
    <li className="rounded-xl border border-primary/40 bg-card p-3.5" data-testid="patient-doctor-question">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold">{t('question.title')}</span>
        {open && !question.patient_reply
          ? <Badge variant="secondary" className="rounded-full text-[12px] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)]">{t('question.chip.waiting')}</Badge>
          : chip(question.status)}
      </div>
      <p className="text-sm leading-6 mt-1 text-foreground">{question.message}</p>
      {question.patient_reply ? (
        <p className="text-sm leading-6 mt-1.5 text-muted-foreground"><span className="font-medium">{t('question.yourAnswer')}</span> {question.patient_reply}</p>
      ) : open && (
        <div className="mt-2 space-y-2">
          <Textarea value={reply} onChange={(e) => setReply(e.target.value)} maxLength={1000}
            placeholder={t('question.placeholder')} className="rounded-xl text-[15px]" data-testid="patient-doctor-question-reply" />
          <Button className="w-full h-11 rounded-xl" onClick={answer} disabled={busy} data-testid="patient-doctor-question-send">
            {busy ? t('revision.sending') : t('question.send')}
          </Button>
        </div>
      )}
    </li>
  );
}

function fmtWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch { return null; }
}

export default function PatientStatus() {
  const { t } = useLang();
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Stripe return handling: /p/{token}/status?session_id=...
  const sessionIdRef = useRef(new URLSearchParams(window.location.search).get('session_id'));
  const [paymentState, setPaymentState] = useState(sessionIdRef.current ? 'checking' : null); // checking | paid | expired | timeout

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/patient/by-token/${token}/status`);
      setData(r.data);
      setError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) setError(detail);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Poll Stripe checkout status after returning from the hosted payment page.
  // tRef keeps the poll from restarting (and resetting attempts) on language switch.
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    const sid = sessionIdRef.current;
    if (!sid) return undefined;
    let stopped = false;
    let attempts = 0;
    const poll = async () => {
      if (stopped) return;
      if (attempts >= 8) { setPaymentState('timeout'); return; }
      attempts += 1;
      try {
        const r = await api.get(`/patient/by-token/${token}/checkout/status/${sid}`);
        if (r.data.payment_status === 'paid') {
          setPaymentState('paid');
          toast.success(tRef.current('status.payReceived'));
          window.history.replaceState({}, '', window.location.pathname);
          load();
          return;
        }
        if (r.data.status === 'expired') { setPaymentState('expired'); return; }
      } catch {
        // transient error — keep polling
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [token, load]);

  if (error && !data) return <PatientError message={error} />;
  if (!data) return <PatientLoading />;

  const { steps = [], draft, can_download: canDownload, expected_turnaround_days: turnaround, all_done: allDone, outcome } = data;
  // LB-LIFE-4. The provider decided not to certify: the timeline has stopped and
  // the next step is a conversation with the office, not a wait. What they decided
  // and why is deliberately NOT here — the practice explains that themselves.
  const declined = outcome === 'declined';

  // Localize timeline steps by key (backend supplies English + a variant hint)
  const stepLabel = (st) => t(`timeline.${st.key}.label`);
  const stepDesc = (st) => {
    if (st.key === 'received' && st.variant === 'draft') return t('timeline.received.descDraft');
    if (st.key === 'sent') {
      if (st.variant === 'self') return t('timeline.sent.descSelf');
      const m = (data.delivery_method || '').toLowerCase();
      const method = m.includes('fax') ? t('method.fax') : t('method.email');
      return t('timeline.sent.desc', { method });
    }
    return t(`timeline.${st.key}.desc`);
  };

  const footer = draft ? (
    <Button className="w-full h-14 rounded-xl text-base font-semibold" onClick={() => navigate(`/p/${token}/form`)} data-testid="patient-status-continue-form-button">
      {t('status.continueForm')} <ArrowRight className="h-5 w-5 ml-1.5" />
    </Button>
  ) : canDownload ? (
    <Button className="w-full h-14 rounded-xl text-base font-semibold" onClick={() => navigate(`/p/${token}/document`)} data-testid="patient-status-download-button">
      <FileCheck2 className="h-5 w-5 mr-1.5" /> {t('status.getDoc')}
    </Button>
  ) : null;

  return (
    <PatientLayout
      practice={data.practice}
      right={<HelpDrawer practice={data.practice} token={token} triggerTestId="patient-status-need-help-button" />}
      footer={footer}
    >
      <div className="space-y-6">
        {paymentState === 'checking' && (
          <div className="rounded-2xl border border-primary/30 bg-accent text-accent-foreground p-4 flex items-center gap-3" data-testid="patient-payment-checking-banner">
            <Loader2 className="h-5 w-5 animate-spin shrink-0" />
            <div className="text-[15px] leading-6">{t('status.payChecking')}</div>
          </div>
        )}
        {paymentState === 'timeout' && (
          <div className="rounded-2xl border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-4 flex items-start gap-3" data-testid="patient-payment-timeout-banner">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-[15px] leading-6">{t('status.payTimeout')}</div>
          </div>
        )}
        {paymentState === 'expired' && (
          <div className="rounded-2xl border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-4 flex items-start gap-3" data-testid="patient-payment-expired-banner">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-[15px] leading-6">
              {t('status.payExpired')}{' '}
              <button type="button" className="font-semibold underline" onClick={() => navigate(`/p/${token}/form`)} data-testid="patient-payment-retry-link">{t('status.tryAgain')}</button>
            </div>
          </div>
        )}
        {declined && (
          <div className="rounded-2xl border bg-muted p-4 flex items-start gap-3" data-testid="patient-status-declined-banner">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-[15px] leading-6">{t('status.declined')}</div>
          </div>
        )}
        <div>
          <h1 className="patient-hero-title text-3xl font-bold leading-tight" data-testid="patient-status-title">{t('status.title')}</h1>
          <p className="text-[15px] text-muted-foreground leading-6 mt-2">
            {declined ? t('status.declinedLead') : allDone ? t('status.allDone') : draft ? t('status.draft') : t('status.auto')}
          </p>
        </div>

        {!draft && !allDone && !declined && (
          <Badge variant="secondary" className="rounded-full px-3 py-1.5 text-[13px] font-medium" data-testid="patient-status-turnaround">
            <Clock className="h-3.5 w-3.5 mr-1.5" /> {t('status.turnaround', { days: turnaround })}
          </Badge>
        )}

        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <ol className="relative" data-testid="patient-status-timeline">
              <span className="absolute left-[11px] top-3 bottom-3 w-px bg-border" aria-hidden="true" />
              {steps.map((st) => (
                <li key={st.key} className="relative flex gap-4 pb-7 last:pb-0" data-testid="patient-status-step" data-state={st.state}>
                  <span
                    className={cn(
                      'relative z-10 mt-1 h-[22px] w-[22px] shrink-0 rounded-full grid place-items-center',
                      st.state === 'done' && 'bg-primary text-primary-foreground',
                      st.state === 'current' && 'bg-primary text-primary-foreground ring-4 ring-primary/15',
                      st.state === 'upcoming' && 'bg-muted border border-border'
                    )}
                  >
                    {st.state === 'done' && <Check className="h-3 w-3" />}
                    {st.state === 'current' && <span className="h-2 w-2 rounded-full bg-primary-foreground animate-pulse" />}
                  </span>
                  <div className="min-w-0">
                    <div className={cn('font-semibold text-[16px] leading-6', st.state === 'upcoming' && 'text-muted-foreground font-medium')}>
                      {stepLabel(st)}
                    </div>
                    <div className="text-sm text-muted-foreground leading-6">{stepDesc(st)}</div>
                    {st.at && <div className="text-xs text-muted-foreground/80 mt-0.5">{fmtWhen(st.at)}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* No "request a change" on a closed case: it would raise a request nobody
            can action. The banner above sends them to the office instead. */}
        {/* LB-CURE. Above the change request on purpose: an employer deadline is
            the only thing on this page with a clock on it. */}
        {!declined && (
          <EmployerCureCard
            token={token}
            canReport={!!data.can_report_employer}
            cure={data.employer_cure}
            requests={(data.revision_requests || []).filter((r) => r.category === 'employer_insufficient' && r.status === 'open')}
            onSubmitted={load}
          />
        )}
        {/* Employer cures are revision requests underneath — that is what makes the
            R21 re-sign unlock work — but they already have their own card above,
            and listing them twice reads as two separate things happening. */}
        {!declined && (
          <RevisionCard
            token={token}
            revisions={(data.revision_requests || []).filter((r) => r.category !== 'employer_insufficient')}
            draft={draft}
            onSubmitted={load}
          />
        )}

        {/* Only while the form is still with the patient: after that the link can
            no longer change the employer's form (the server answers 409), and the
            way in is "Request a change" or, once delivered, the employer card. */}
        {draft && <Card className="rounded-2xl">
          <CardContent className="p-5 flex items-start gap-3.5">
            <div className="h-11 w-11 rounded-xl bg-accent text-accent-foreground grid place-items-center shrink-0">
              <ScanLine className="h-5 w-5" />
            </div>
            <div className="text-sm text-muted-foreground leading-6">
              <span className="font-semibold text-foreground">{t('status.hrFormTitle')}</span>{' '}
              {t('status.hrFormBody')}
            </div>
          </CardContent>
        </Card>}
      </div>
    </PatientLayout>
  );
}
